import { createSlice, createAsyncThunk, createSelector } from '@reduxjs/toolkit'
import { hasAlarmExpired, createAlarmTimeSecondsFromNow, createAlarmTimeForTimeOfDay, getCurrentTimeOfDay } from '../../alarm/index.js'

// All four story paths share identical reducer logic and selectors; they differ
// only in the story data, which phase they start/resume on (conversation vs
// marker), and which scenes skip straight to the next one. This factory builds a
// slice + selectors from that small config so the behaviour lives in one place.
export function createStoryPathSlice ({ name, stories, hasConversation = true, scenesThatGoStraightToNextScene = [] }) {
  const initialState = {
    // 0 will be scene 1
    sceneIndex: 0,
    showConversation: hasConversation,
    showMarker: !hasConversation,
    showScene: false,
    nextAlarmTimestamp: null,
    scenesThatGoStraightToNextScene
  }

  const skipToNextScene = createAsyncThunk(`${name}/skipToNextScene`, async () => {
    await new Promise((resolve) => setTimeout(resolve, 500))
  })

  const slice = createSlice({
    name,
    initialState,
    reducers: {
      goToConversation: (state) => {
        state.showConversation = true
      },
      showMarker: (state) => {
        state.showMarker = true
        state.showConversation = false
      },
      // When the user clicks on a marker and "goes to it". Will display a scene.
      goToMarker: (state) => {
        state.showScene = true
        state.showMarker = false
      },
      timerTick: (state) => {
        if (state.nextAlarmTimestamp && hasAlarmExpired(state.nextAlarmTimestamp)) {
          state.nextAlarmTimestamp = null
          if (hasConversation) {
            state.showConversation = true
          } else {
            state.showMarker = true
          }
          state.sceneIndex += 1
        }
      },
      endScene: (state, action) => {
        state.showScene = false
        state.nextAlarmTimestamp = action.payload
          ? createAlarmTimeForTimeOfDay(action.payload)
          : createAlarmTimeSecondsFromNow(1)
      },
      debugFireAlarm: (state) => {
        if (state.nextAlarmTimestamp) {
          state.nextAlarmTimestamp = Date.now() - 1
        }
      }
    },
    extraReducers: (builder) => {
      builder.addCase(skipToNextScene.pending, (state) => {
        state.showScene = false
      })
      builder.addCase(skipToNextScene.fulfilled, (state) => {
        state.showScene = true
        state.sceneIndex += 1
      })
    }
  })

  const selectSelf = (state) => state[name]
  const selectStory = (state) => stories[selectSelf(state).sceneIndex]

  // Until the player advances time-of-day for the first time in free play,
  // only Scientific Detective is reachable at all - every other path is fully
  // locked out (markers and conversations alike), making the opening slot a
  // single-storyline intro.
  const isPathAllowedInFreePlay = (state) => {
    if (!state.settings.isNoDelay || state.settings.freePlayHasAdvancedOnce) return true
    return name === 'scientificDetective'
  }

  // Conversations are only gated by time-of-day in free play (they're free
  // play's equivalent of the real clock delay timescale mode already has via
  // scheduled alarms). Timescale conversations fire as soon as their scene is
  // reached, unchanged from before this gate existed.
  const isCurrentFreePlayTimeOfDay = (state) => {
    if (!isPathAllowedInFreePlay(state)) return false
    const story = selectStory(state)
    if (!state.settings.isNoDelay || !story?.timeOfDay) return true
    if (story.timeOfDay !== state.settings.freePlayTimeOfDay) return false
    // Mirrors selectMarker below: finishing any Reader scene uses up the
    // current time slot globally, so a conversation for this same slot
    // shouldn't pop up again until the player advances to the next one.
    return !state.settings.freePlayTimeSlotConsumed
  }

  const selectors = {
    selectScene: (state) => selectSelf(state).showScene && selectStory(state)?.scene,
    selectSceneArt: (state) => selectSelf(state).showScene && selectStory(state)?.sceneArt,
    selectIsShowScene: (state) => selectSelf(state).showScene,
    selectIsShowConversation: (state) => !!(selectSelf(state).showConversation && selectStory(state)?.conversation && isCurrentFreePlayTimeOfDay(state)),
    selectMarker: (state) => {
      const story = selectStory(state)
      if (!selectSelf(state).showMarker || !story?.marker) return false
      if (!isPathAllowedInFreePlay(state)) return false
      // A marker is only visible while its scene's time-of-day slot is the
      // current one (e.g. a "morning" marker shows 7am–12pm). In timescale mode
      // that's the real clock; in free play it's the player-advanced slot
      // (there's no wall clock to key off in free play).
      if (story.timeOfDay) {
        const isFreePlay = state.settings.isNoDelay
        const currentTimeOfDay = isFreePlay
          ? state.settings.freePlayTimeOfDay
          // debugTimeOfDay lets the debug panel pretend it's a given slot.
          : (state.settings.debugTimeOfDay ?? getCurrentTimeOfDay())
        if (story.timeOfDay !== currentTimeOfDay) return false
        // In free play, finishing any Reader scene uses up the current time
        // slot globally - no more map markers for this slot until the player
        // advances to the next one.
        if (isFreePlay && state.settings.freePlayTimeSlotConsumed) return false
      }
      return story.marker
    },
    selectConversation: (state) => (selectSelf(state).showConversation && isCurrentFreePlayTimeOfDay(state)) && selectStory(state)?.conversation,
    selectNextAlarmTimestamp: (state) => selectSelf(state).nextAlarmTimestamp,
    selectNextTimeOfDay: (state) => stories[selectSelf(state).sceneIndex + 1]?.timeOfDay,
    selectTeaser: (state) => {
      const s = selectSelf(state)
      const rawIdle = !s.showMarker && !s.showScene && !s.showConversation
      // In free play, finishing a scene fires a 1s internal alarm that bumps
      // sceneIndex regardless of whether the *next* scene's marker/conversation
      // is actually visible - it's commonly hidden behind the free-play
      // time-of-day/slot-consumed/path-lock gates (see selectMarker/
      // selectIsShowConversation above) until the player manually advances
      // time. When that's happened, sceneIndex has silently moved on to a
      // scene the player hasn't seen or engaged with at all, so fall back to
      // the last one actually completed.
      const isGatedHidden = !rawIdle && !s.showScene && !selectors.selectMarker(state) && !selectors.selectIsShowConversation(state)
      // teasers[i] is a cliffhanger hook written for the wait *after* finishing
      // scene i (e.g. scenesThatGoStraightToNextScene entries have a null
      // teaser, since there's no wait to fill). Index -1 for a path that's
      // never had a scene completed at all resolves to undefined, which is
      // exactly the blank we want before the player has read anything here.
      const index = isGatedHidden ? s.sceneIndex - 1 : s.sceneIndex
      return stories[index]?.teaser
    },
    selectDoesSceneGoStraightToNextScene: (state) => {
      const s = selectSelf(state)
      return s.scenesThatGoStraightToNextScene.includes(s.sceneIndex)
    },
    selectNotification: (state) => {
      const s = selectSelf(state)
      const stepNum = s.sceneIndex + 1 
      const conversation = stories[stepNum]?.conversation
      if (!conversation || !conversation.messages?.length) return
      const notification = {
        from: conversation.from,
        message: conversation.messages[0].text
      }
      return notification
    }
  }

  return {
    name,
    reducer: slice.reducer,
    actions: slice.actions,
    skipToNextScene,
    selectors
  }
}
